import type { ReactElement } from 'react'
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { Button } from './Button.tsx'
import { paginationWindow } from '../lib/pagination.ts'

/**
 * Numbered pages with an ellipsis window, ported from V1.
 *
 * Not prev/next alone: a pharmacist working a filtered queue wants to know how
 * much is left and to jump, and "Anterior / Próxima" answers neither. Hidden
 * entirely at one page, because a single page needs no navigation.
 */
export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}): ReactElement | null {
  if (totalPages <= 1) return null

  const items = paginationWindow(currentPage, totalPages)
  const atFirst = currentPage <= 1
  const atLast = currentPage >= totalPages

  return (
    <nav aria-label="Paginação" className="flex items-center justify-center gap-2">
      <Button
        variant="tertiary"
        tone="brand"
        size="small"
        leadingIcon={faArrowLeft}
        disabled={atFirst}
        onClick={() => onPageChange(currentPage - 1)}
        aria-label="Página anterior"
        className="size-8 px-0"
      />

      {items.map((item) =>
        item.type === 'ellipsis' ? (
          <span
            key={item.key}
            aria-hidden="true"
            className="text-tP text-icon-neutral inline-flex w-8 justify-center"
          >
            …
          </span>
        ) : (
          <Button
            key={item.page}
            variant={item.page === currentPage ? 'primary' : 'secondary'}
            tone={item.page === currentPage ? 'brand' : 'neutral'}
            size="small"
            onClick={() => onPageChange(item.page)}
            aria-label={`Página ${item.page}`}
            aria-current={item.page === currentPage ? 'page' : undefined}
            className={
              item.page === currentPage ? 'w-8 self-auto px-0' : 'bg-fill-white! w-8 self-auto px-0'
            }
          >
            {item.page}
          </Button>
        ),
      )}

      <Button
        variant="tertiary"
        tone="brand"
        size="small"
        leadingIcon={faArrowRight}
        disabled={atLast}
        onClick={() => onPageChange(currentPage + 1)}
        aria-label="Próxima página"
        className="size-8 px-0"
      />
    </nav>
  )
}
